import { Card } from "@rumbledirect/ui";
import {
  EvaluationGroupDetail,
  EvaluationRule,
  EvaluationRuleSet,
  TranslatableEnumValue,
} from "@rumble-it/common-lib/types/models/EvaluationGroup.interface";
import {
  formatTranslatableEnum,
  formatTranslatableEnumList,
} from "../lib/helpers/formatTranslatableEnumList";
import { Descriptions, TableColumnsType } from "antd";
import { Table } from "@/components/v2";

interface EvaluationGroupDetailCardProps {
  evaluationGroup: EvaluationGroupDetail | undefined;
  isLoading?: boolean;
}

const ruleColumns: TableColumnsType<EvaluationRule> = [
  {
    title: "Naam",
    dataIndex: "name",
    key: "name",
  },
  {
    title: "Expressie",
    dataIndex: "expression",
    key: "expression",
    render: (expression: string) => (
      <code className="text-xs whitespace-pre-wrap">{expression}</code>
    ),
  },
  {
    title: "Output",
    dataIndex: "output",
    key: "output",
    render: (output: Record<string, unknown>) => (
      <code className="text-xs whitespace-pre-wrap">
        {JSON.stringify(output, null, 2)}
      </code>
    ),
  },
];

const ruleSetColumns: TableColumnsType<EvaluationRuleSet> = [
  {
    title: "Naam",
    dataIndex: "name",
    key: "name",
  },
  {
    title: "Type",
    dataIndex: "type",
    key: "type",
    render: (type: TranslatableEnumValue) => formatTranslatableEnum(type),
  },
  {
    title: "Voorwaarde",
    dataIndex: "precondition",
    key: "precondition",
    render: (precondition: string | null) =>
      precondition ? (
        <code className="text-xs whitespace-pre-wrap">{precondition}</code>
      ) : (
        "-"
      ),
  },
  {
    title: "Verbergen in admin",
    dataIndex: "hideInAdmin",
    key: "hideInAdmin",
    render: (hideInAdmin: boolean) => (hideInAdmin ? "Ja" : "Nee"),
    width: 140,
  },
  {
    title: "Resultaat in output",
    dataIndex: "includeResultInOutput",
    key: "includeResultInOutput",
    render: (includeResultInOutput: boolean) =>
      includeResultInOutput ? "Ja" : "Nee",
    width: 150,
  },
  {
    title: "Regels",
    key: "rulesCount",
    render: (_: unknown, record: EvaluationRuleSet) => record.rules.length,
    width: 80,
  },
];

export const EvaluationGroupDetailCard = ({
  evaluationGroup,
  isLoading,
}: EvaluationGroupDetailCardProps) => {
  return (
    <div className="space-y-4">
      <Card loading={isLoading} className="w-full" title="Evaluatiegroep">
        <Descriptions
          column={1}
          items={[
            {
              label: "Naam",
              children: evaluationGroup?.name ?? null,
            },
            {
              label: "Input types",
              children: formatTranslatableEnumList(evaluationGroup?.inputTypes),
            },
            {
              label: "Context types",
              children: formatTranslatableEnumList(
                evaluationGroup?.contextTypes?.map(({ type }) => type),
              ),
            },
            {
              label: "Output types",
              children: formatTranslatableEnumList(
                evaluationGroup?.outputTypes,
              ),
            },
          ]}
        />
      </Card>

      <Card loading={isLoading} className="w-full" title="Rulesets en regels">
        <Table
          rowKey="id"
          columns={ruleSetColumns}
          dataSource={evaluationGroup?.ruleSets}
          pagination={false}
          expandable={{
            expandedRowRender: (ruleSet) => (
              <Table
                rowKey="id"
                columns={ruleColumns}
                dataSource={ruleSet.rules}
                pagination={false}
                size="small"
              />
            ),
            rowExpandable: (ruleSet) => ruleSet.rules.length > 0,
          }}
        />
      </Card>
    </div>
  );
};
